import * as Sentry from '@sentry/react'
import { Breadcrumb } from '@sentry/types/types/breadcrumb'
import { Event } from '@sentry/types/types/event'
import { Request } from '@sentry/types/types/request'
import { SeverityLevel } from '@sentry/types/types/severity'
import React from 'react'
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from 'react-router-dom'

export const needSendAnyway = Symbol('needSendAnyway')
type TronErrorEvent = { code: number; message: string }
const defaultTronErrorEvents = new Map<number, Set<string>>([
  [4001, new Set(['User rejected the request.', 'User rejected the request', 'wallet must has at least one account'])],
  [4100, new Set(['Unauthorized'])],
  [4200, new Set(['Unsupported Method'])],
  [4900, new Set(['Unsupported Method'])],
  [4901, new Set(['Chain Disconnected'])],
])

export const initSentry = (): void => {
  const skipLevels = new Set(['warning', 'log', 'info', 'debug'])
  const skipSource = new Set(['route', 'ui.click'])
  const skipCategory = new Set(['fetch', 'sentry.event'])
  const skipMessages = new Set(['useGetShariaLaw [object Response]'])

  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  const propagationTargets: string[] = ['https://daisy.global']
  const needSkipLevel = (e: { level?: SeverityLevel; [needSendAnyway]?: boolean }) =>
    !e.level || (!e[needSendAnyway] && skipLevels.has(e.level))
  const needSkipSource = (e: Event) => !e.transaction_info?.source || skipSource.has(e.transaction_info?.source)
  const needSkipCategory = (e: Breadcrumb) => !!e.category && skipCategory.has(e.category)
  const needSkipMessages = (e: Breadcrumb) => !!e.message && skipMessages.has(e.message)
  const needSkipUrl = ({ request }: { request?: Request }) =>
    !!request?.url && propagationTargets.every((target) => !request.url?.startsWith(target))
  const needSkipUnhandledTronEvent = (event: Event): boolean => {
    try {
      const errorEvent = event?.extra?.__serialized__ as TronErrorEvent | undefined
      return !!errorEvent && !!defaultTronErrorEvents.get(errorEvent.code)?.has(errorEvent.message)
    } catch (e) {
      return false
    }
  }
  const cleanUpEvent = (event: Event) => {
    if (needSendAnyway in event) {
      delete (event as { [needSendAnyway]: unknown })[needSendAnyway]
    }
    return event
  }

  Sentry.init({
    dsn: 'https://4095fe6a1faa4b29f2fc403a81326840@o4506257706975232.ingest.sentry.io/4506257709793280',
    integrations: [
      new Sentry.BrowserTracing({
        tracePropagationTargets: propagationTargets,
        routingInstrumentation: Sentry.reactRouterV6Instrumentation(
          React.useEffect,
          useLocation,
          useNavigationType,
          createRoutesFromChildren,
          matchRoutes,
        ),
      }),
    ],
    // Performance Monitoring
    tracesSampleRate: 1.0, // Capture 100% of the transactions
    // Session Replay
    replaysSessionSampleRate: 0, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    beforeSend: (event) =>
      needSkipUrl(event) || needSkipLevel(event) || needSkipUnhandledTronEvent(event) ? null : cleanUpEvent(event),
    beforeSendTransaction: (event) =>
      needSkipUrl(event) || needSkipLevel(event) || needSkipSource(event) ? null : event,
    beforeBreadcrumb: (event) =>
      needSkipLevel(event) || needSkipCategory(event) || needSkipMessages(event) ? null : event,
  })
}
